/**
 * API client for backend communication
 * Uses the platform module for token management
 */

import { getAccessToken, getViewAsUserId } from '../platform';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

// Global auth error handler (set by auth provider)
let authErrorHandler: (() => void) | null = null;
let authErrorFired = false;

export function setAuthErrorHandler(handler: () => void) {
  authErrorHandler = handler;
  authErrorFired = false; // Reset when handler is re-registered (e.g. after login)
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
  request_id?: string;
}

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  timestamp: string;
}

export interface ReadyResponse {
  status: string;
  service: string;
  checks: {
    database: string;
    storage: string;
  };
}

// ── Skills marketplace types ────────────────────────────────────────────────
export interface SkillCategory {
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  description?: string;
  icon?: string;
  accent?: string;
  skill_count?: number;
}

export interface SkillSummary {
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  description?: string;
  value_props?: string[];
  version?: string;
  emoji?: string;
  icon?: string;
  compatible_tools?: string[];
  pricing_model?: string;
  price_one_time_cents?: number | null;
  featured?: boolean;
  author?: string;
  install_count?: number;
  updated_at?: string;
  category?: { id: string; slug: string; name: string; accent?: string } | null;
  owned?: boolean;
}

export interface SkillGuide {
  tool: string;
  title?: string;
  steps: string;
}

export interface SkillFile {
  path: string;
  content: string;
  is_primary?: boolean;
}

export interface SkillFileMeta {
  path: string;
  is_primary?: boolean;
  bytes?: number;
  content?: string;
}

export interface SkillDetail extends SkillSummary {
  what_you_get?: string;
  guides: SkillGuide[];
  files: SkillFileMeta[];
  preview: string;
  install_command: string;
  category?: { id: string; slug: string; name: string; tagline?: string; accent?: string } | null;
}

export interface SkillOutput {
  id: string;
  title?: string;
  input?: string | null;
  output: string;
  profile_version?: number | null;
  created_at: string;
}

// ── Company profile (the spine skills run against) ──────────────────────────
export type ProfileFieldSource = 'website' | 'founder' | 'skill';

export interface CompanyProfile {
  id: string;
  user_id: string;
  website_url?: string | null;
  company_name?: string | null;
  one_liner?: string | null;
  icp?: string | null;
  positioning?: string | null;
  pricing?: string | null;
  competitors?: string[];
  stage?: string | null;
  primary_goal?: string | null;
  field_meta?: Record<string, { source: ProfileFieldSource; confidence: number; updated_at: string }>;
  version?: number;
  updated_at?: string;
}

export interface PresignUploadRequest {
  filename: string;
  contentType: string;
  entityType: string;
  entityId: string;
}

export interface PresignUploadResponse {
  url: string;
  key: string;
  expiresIn: number;
}

export interface PresignDownloadRequest {
  key: string;
}

export interface PresignDownloadResponse {
  url: string;
  expiresIn: number;
  filename: string;
  contentType: string;
}

export interface UserResponse {
  user: {
    userId: string;
    email?: string;
    name?: string;
  };
  message: string;
}

export interface DemoItem {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'completed' | 'archived';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ItemsListResponse {
  data: DemoItem[];
}

export interface ItemResponse {
  data: DemoItem;
}

// ============================================
// Sources Types
// ============================================

export interface TldvMeeting {
  id: string;
  name: string;
  happenedAt: string;
  duration: number;
  url: string;
}

export interface PickerItem {
  id: string;
  name: string;
  date?: string;
  subtitle?: string;
}

export interface Source {
  id: string;
  user_id: string;
  type: 'google_drive' | 'google_meet' | 'custom_note' | 'tldv' | 'fathom' | 'notion' | 'gong' | 'fireflies' | 'otter' | 'lindy' | 'avoma' | 'granola' | 'zoom';
  name: string;
  config?: Record<string, unknown>;
  content?: string;
  status: 'active' | 'needs_auth' | 'error' | 'disabled';
  error_message?: string;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SourcesListResponse {
  data: Source[];
}

export interface SourceResponse {
  data: Source;
}

// ============================================
// Knowledge Types
// ============================================

export interface KnowledgeItem {
  id: string;
  user_id: string;
  type: 'insight' | 'story' | 'metric' | 'framework' | 'opinion' | 'quote' | 'lesson' | 'anecdote';
  title: string;
  summary: string;
  details?: string;
  topics: string[];
  entities?: Record<string, unknown>[];
  time_context?: string;
  confidence?: number;
  source_id?: string;
  chunk_id?: string;
  snippet?: string;
  source_item_name?: string;
  status: 'active' | 'archived' | 'deleted';
  prompt_version?: string;
  created_at: string;
  updated_at: string;
  sources?: { name: string; type: string };
}

export interface KnowledgeListResponse {
  data: KnowledgeItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface KnowledgeItemResponse {
  data: KnowledgeItem;
}

export interface KnowledgeTypesResponse {
  data: Array<{ type: string; count: number }>;
}

export interface KnowledgeTopicsResponse {
  data: Array<{ topic: string; count: number }>;
}

// ============================================
// Generation Context (perspective/instructions for post creation)
// ============================================

export interface GenerationContext {
  perspective?: string;
  additionalContext?: string;
}

// ============================================
// LinkedIn Types
// ============================================

export interface LinkedInConnection {
  connected: boolean;
  displayName?: string;
  email?: string;
  profileImageUrl?: string;
  expiresAt?: string;
  daysUntilExpiry?: number;
  status?: 'active' | 'expired' | 'disconnected';
}

// ============================================
// Posts Types
// ============================================

export interface PostDraft {
  id: string;
  user_id: string;
  title?: string;
  body: string;
  hook_variants: string[];
  cta_variants: string[];
  hashtags: string[];
  format: string;
  length_preset: 'short' | 'medium' | 'long';
  tone_preset: 'pragmatic' | 'technical' | 'story' | 'framework';
  status: 'idea' | 'draft' | 'ready_to_publish' | 'published' | 'archived';
  knowledge_item_ids: string[];
  published_at?: string;
  published_url?: string;
  publish_notes?: string;
  linkedin_post_urn?: string;
  publish_channel?: 'manual' | 'linkedin';
  prompt_version?: string;
  current_version: number;
  show_attribution?: boolean;
  created_at: string;
  updated_at: string;
  versions?: PostVersion[];
  knowledge_items?: KnowledgeItem[];
}

export interface PostVersion {
  id: string;
  post_draft_id: string;
  version_number: number;
  body: string;
  source: 'user_edit' | 'regenerate' | 'initial';
  regenerate_options?: Record<string, unknown>;
  created_at: string;
}

export interface PostsListResponse {
  data: PostDraft[];
  total: number;
  limit: number;
  offset: number;
}

export interface PostResponse {
  data: PostDraft;
  usage?: { inputTokens: number; outputTokens: number };
}

export interface PostStatsResponse {
  data: {
    total: number;
    by_status: Record<string, number>;
  };
}

// ============================================
// Weekly Insight Types
// ============================================

export interface WeeklyInsight {
  id: string;
  user_id: string;
  week_start: string;
  knowledge_item_id: string | null;
  suggested_post: string;
  suggested_title: string | null;
  status: 'pending' | 'accepted' | 'dismissed';
  accepted_post_id: string | null;
  created_at: string;
  knowledge_items?: {
    id: string;
    type: string;
    title: string;
    summary: string;
    source_id?: string;
  };
}

export interface WeeklyInsightResponse {
  data: WeeklyInsight | null;
}

export interface WeeklyInsightAcceptResponse {
  data: {
    insight_id: string;
    post: PostDraft;
  };
}

// ============================================
// Onboarding Types
// ============================================

export interface OnboardingStatus {
  completed: boolean;
  current_step: number;
  has_voice_profile: boolean;
  topics_include: string[];
  topics_exclude: string[];
  counts: {
    sources: number;
    voice_samples: number;
    knowledge_items: number;
  };
}

export interface OnboardingStatusResponse {
  data: OnboardingStatus;
}

export interface VoiceProfile {
  tone: string;
  style: string;
  vocabulary: {
    preferred: string[];
    avoid: string[];
  };
  patterns: string[];
  signaturePhrases: string[];
  sentenceStructure: string;
  formality: 'casual' | 'balanced' | 'formal';
  narrativeStyle: 'direct' | 'narrative' | 'mixed';
}

export interface VoiceProfileResponse {
  data: {
    voice_profile: VoiceProfile;
    usage: { inputTokens: number; outputTokens: number };
  };
}

class ApiClient {
  private baseUrl: string;
  // In-flight request cache to prevent duplicate concurrent requests
  private inFlightRequests: Map<string, Promise<ApiResponse<unknown>>> = new Map();

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // Deduplicate GET requests that are in flight
  private async deduplicatedRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<ApiResponse<T>>
  ): Promise<ApiResponse<T>> {
    const existing = this.inFlightRequests.get(cacheKey);
    if (existing) {
      return existing as Promise<ApiResponse<T>>;
    }

    const promise = requestFn().finally(() => {
      this.inFlightRequests.delete(cacheKey);
    });
    this.inFlightRequests.set(cacheKey, promise as Promise<ApiResponse<unknown>>);
    return promise;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add auth token from platform module
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add view-as header for admin impersonation
    const viewAsUserId = getViewAsUserId();
    if (viewAsUserId) {
      headers['X-View-As-User'] = viewAsUserId;
    }

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...options,
        headers,
      });

      // Handle 204 No Content (e.g. DELETE responses) — no JSON body to parse
      if (response.status === 204) {
        return { data: undefined as unknown as T };
      }

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired or invalid
        // Guard: only fire once to prevent multiple concurrent 401s from racing
        if (response.status === 401 && authErrorHandler && !authErrorFired) {
          authErrorFired = true;
          authErrorHandler();
        }

        return {
          error: data.error || { code: 'UNKNOWN_ERROR', message: 'Request failed' },
          request_id: data.request_id,
        };
      }

      return { data, request_id: data.request_id };
    } catch (error) {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  // Health endpoints
  async healthz(): Promise<ApiResponse<HealthResponse>> {
    return this.request<HealthResponse>('/healthz');
  }

  async readyz(): Promise<ApiResponse<ReadyResponse>> {
    return this.request<ReadyResponse>('/readyz');
  }

  // Auth endpoints
  async me(): Promise<ApiResponse<UserResponse>> {
    return this.request<UserResponse>('/api/me');
  }

  async getMe(): Promise<ApiResponse<UserResponse>> {
    return this.me();
  }

  // File endpoints
  async presignUpload(data: PresignUploadRequest): Promise<ApiResponse<PresignUploadResponse>> {
    return this.request<PresignUploadResponse>('/files/presign-upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async presignDownload(data: PresignDownloadRequest): Promise<ApiResponse<PresignDownloadResponse>> {
    return this.request<PresignDownloadResponse>('/files/presign-download', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Upload file directly to R2 using presigned URL
  async uploadFile(presignedUrl: string, file: File): Promise<boolean> {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // Items (Supabase demo)
  async listItems(): Promise<ApiResponse<ItemsListResponse>> {
    return this.request<ItemsListResponse>('/api/items');
  }

  async createItem(data: { name: string; description?: string; status?: string }): Promise<ApiResponse<ItemResponse>> {
    return this.request<ItemResponse>('/api/items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateItem(id: string, data: { name?: string; description?: string; status?: string }): Promise<ApiResponse<ItemResponse>> {
    return this.request<ItemResponse>(`/api/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteItem(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/items/${id}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Sources API
  // ============================================

  async listSources(params?: { type?: string; status?: string }): Promise<ApiResponse<SourcesListResponse>> {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.status) query.set('status', params.status);
    const queryStr = query.toString();
    const path = `/api/sources${queryStr ? `?${queryStr}` : ''}`;
    // Deduplicate concurrent requests
    return this.deduplicatedRequest(`GET:${path}`, () =>
      this.request<SourcesListResponse>(path)
    );
  }

  async getSource(id: string): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>(`/api/sources/${id}`);
  }

  async createSource(data: {
    type: Source['type'];
    name: string;
    content?: string;
    config?: Record<string, unknown>;
  }): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>('/api/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSource(id: string, data: {
    name?: string;
    content?: string;
    config?: Record<string, unknown>;
    status?: 'active' | 'disabled';
  }): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>(`/api/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSource(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/sources/${id}`, {
      method: 'DELETE',
    });
  }

  async getSourcePreview(id: string, itemName?: string): Promise<ApiResponse<{ data: { content: string; source_name: string } }>> {
    const params = itemName ? `?item_name=${encodeURIComponent(itemName)}` : '';
    return this.request(`/api/sources/${id}/preview${params}`);
  }

  async syncSource(id: string): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>(`/api/sources/${id}/sync`, {
      method: 'POST',
    });
  }

  // ============================================
  // Knowledge API
  // ============================================

  async listKnowledge(params?: {
    query?: string;
    type?: string;
    topic?: string;
    source_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<KnowledgeListResponse>> {
    const query = new URLSearchParams();
    if (params?.query) query.set('query', params.query);
    if (params?.type) query.set('type', params.type);
    if (params?.topic) query.set('topic', params.topic);
    if (params?.source_id) query.set('source_id', params.source_id);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const queryStr = query.toString();
    const path = `/api/knowledge${queryStr ? `?${queryStr}` : ''}`;
    // Deduplicate concurrent requests
    return this.deduplicatedRequest(`GET:${path}`, () =>
      this.request<KnowledgeListResponse>(path)
    );
  }

  async getKnowledge(id: string): Promise<ApiResponse<KnowledgeItemResponse>> {
    return this.request<KnowledgeItemResponse>(`/api/knowledge/${id}`);
  }

  async getKnowledgeTypes(): Promise<ApiResponse<KnowledgeTypesResponse>> {
    // Deduplicate concurrent requests
    return this.deduplicatedRequest('GET:/api/knowledge/types', () =>
      this.request<KnowledgeTypesResponse>('/api/knowledge/types')
    );
  }

  async getKnowledgeTopics(): Promise<ApiResponse<KnowledgeTopicsResponse>> {
    // Deduplicate concurrent requests
    return this.deduplicatedRequest('GET:/api/knowledge/topics', () =>
      this.request<KnowledgeTopicsResponse>('/api/knowledge/topics')
    );
  }

  async updateKnowledge(id: string, data: {
    title?: string;
    summary?: string;
    details?: string;
    topics?: string[];
    status?: 'active' | 'archived';
  }): Promise<ApiResponse<KnowledgeItemResponse>> {
    return this.request<KnowledgeItemResponse>(`/api/knowledge/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async archiveKnowledge(id: string): Promise<ApiResponse<KnowledgeItemResponse>> {
    return this.request<KnowledgeItemResponse>(`/api/knowledge/${id}/archive`, {
      method: 'POST',
    });
  }

  // ============================================
  // Posts API
  // ============================================

  async listPosts(params?: {
    status?: string;
    source_id?: string;
    source_item_name?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<PostsListResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.source_id) query.set('source_id', params.source_id);
    if (params?.source_item_name) query.set('source_item_name', params.source_item_name);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const queryStr = query.toString();
    return this.request<PostsListResponse>(`/api/posts${queryStr ? `?${queryStr}` : ''}`);
  }

  async getPost(id: string): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>(`/api/posts/${id}`);
  }

  async getPostStats(): Promise<ApiResponse<PostStatsResponse>> {
    return this.request<PostStatsResponse>('/api/posts/stats');
  }

  async getPostSourceItems(): Promise<ApiResponse<{ data: Array<{ source_id: string; items: string[] }> }>> {
    return this.request<{ data: Array<{ source_id: string; items: string[] }> }>('/api/posts/source-items');
  }

  async createPost(data: {
    title?: string;
    body: string;
    hook_variants?: string[];
    cta_variants?: string[];
    hashtags?: string[];
    length_preset?: 'short' | 'medium' | 'long';
    tone_preset?: 'pragmatic' | 'technical' | 'story' | 'framework';
    knowledge_item_ids?: string[];
  }): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>('/api/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePost(id: string, data: {
    title?: string;
    body?: string;
    hook_variants?: string[];
    cta_variants?: string[];
    hashtags?: string[];
    status?: 'draft' | 'ready_to_publish' | 'archived';
    show_attribution?: boolean;
  }): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>(`/api/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async generatePost(data: {
    knowledge_item_ids: string[];
    topic?: string;
    length_preset?: 'short' | 'medium' | 'long';
    tone_preset?: 'pragmatic' | 'technical' | 'story' | 'framework';
    async?: boolean;
  }): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>('/api/posts/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateBatchPosts(count?: number, sourceIds?: string[], context?: GenerationContext, sourceItemName?: string): Promise<ApiResponse<{ data: PostDraft[]; total: number }>> {
    const payload: { count: number; source_ids?: string[]; source_item_name?: string; context?: GenerationContext } = { count: count || 5 };
    if (sourceIds && sourceIds.length > 0) {
      payload.source_ids = sourceIds;
    }
    if (sourceItemName) {
      payload.source_item_name = sourceItemName;
    }
    if (context && (context.perspective || context.additionalContext)) {
      payload.context = context;
    }
    return this.request<{ data: PostDraft[]; total: number }>('/api/posts/generate-batch', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async regeneratePost(id: string, data: {
    instruction: 'tone' | 'structure' | 'contrarian' | 'concise' | 'story';
    locked_parts?: string[];
  }): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>(`/api/posts/${id}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async publishPost(id: string, data?: {
    url?: string;
    notes?: string;
  }): Promise<ApiResponse<PostResponse>> {
    return this.request<PostResponse>(`/api/posts/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async deletePost(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/posts/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteAllPosts(): Promise<ApiResponse<{ data: { deleted_count: number } }>> {
    return this.request<{ data: { deleted_count: number } }>('/api/posts/all', {
      method: 'DELETE',
    });
  }

  // ============================================
  // Weekly Insight API
  // ============================================

  async getWeeklyInsight(): Promise<ApiResponse<WeeklyInsightResponse>> {
    return this.request<WeeklyInsightResponse>('/api/posts/weekly-insight');
  }

  async acceptWeeklyInsight(id: string): Promise<ApiResponse<WeeklyInsightAcceptResponse>> {
    return this.request<WeeklyInsightAcceptResponse>(`/api/posts/weekly-insight/${id}/accept`, {
      method: 'POST',
    });
  }

  async dismissWeeklyInsight(id: string): Promise<ApiResponse<WeeklyInsightResponse>> {
    return this.request<WeeklyInsightResponse>(`/api/posts/weekly-insight/${id}/dismiss`, {
      method: 'POST',
    });
  }

  // ============================================
  // Onboarding API
  // ============================================

  async getOnboardingStatus(): Promise<ApiResponse<OnboardingStatusResponse>> {
    return this.request<OnboardingStatusResponse>('/api/onboarding/status');
  }

  async updateOnboardingStep(step: number): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/onboarding/step', {
      method: 'POST',
      body: JSON.stringify({ step }),
    });
  }

  async addSeedNotes(notes: Array<{ title: string; content: string }>): Promise<ApiResponse<SourcesListResponse>> {
    return this.request<SourcesListResponse>('/api/onboarding/seed-notes', {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async addVoiceSamples(samples: Array<{ content: string; type: string }>): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/onboarding/voice-samples', {
      method: 'POST',
      body: JSON.stringify({ samples }),
    });
  }

  async generateVoiceProfile(): Promise<ApiResponse<VoiceProfileResponse>> {
    return this.request<VoiceProfileResponse>('/api/onboarding/generate-voice', {
      method: 'POST',
    });
  }

  async getVoiceProfile(): Promise<ApiResponse<{
    data: {
      voice_profile: VoiceProfile | null;
      voice_samples: Array<{
        id: string;
        content: string;
        type: string;
        created_at: string;
      }>;
    };
  }>> {
    return this.request<{
      data: {
        voice_profile: VoiceProfile | null;
        voice_samples: Array<{
          id: string;
          content: string;
          type: string;
          created_at: string;
        }>;
      };
    }>('/api/onboarding/voice-profile');
  }

  async setTopicPreferences(data: {
    topics_include: string[];
    topics_exclude?: string[];
  }): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/onboarding/topics', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async completeOnboarding(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/onboarding/complete', {
      method: 'POST',
    });
  }

  async resetOnboarding(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/onboarding/reset', {
      method: 'POST',
    });
  }

  async fetchLinkedInPost(url: string): Promise<ApiResponse<{ data: { content: string } }>> {
    return this.request<{ data: { content: string } }>('/api/onboarding/fetch-linkedin-post', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async skipOnboarding(): Promise<ApiResponse<{ onboarding_completed: boolean; used_defaults: boolean; message: string }>> {
    return this.request<{ onboarding_completed: boolean; used_defaults: boolean; message: string }>('/api/onboarding/skip', {
      method: 'POST',
    });
  }

  // ============================================
  // Google Drive OAuth API
  // ============================================

  async getGoogleAuthUrl(sourceType?: 'google_drive' | 'google_meet'): Promise<ApiResponse<{ data: { authUrl: string } }>> {
    const params = sourceType ? `?sourceType=${sourceType}` : '';
    return this.request<{ data: { authUrl: string } }>(`/api/google/auth${params}`);
  }

  async disconnectGoogle(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/google/disconnect', {
      method: 'POST',
    });
  }

  async disconnectGoogleMeet(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/google/disconnect-meet', {
      method: 'POST',
    });
  }

  async listGoogleDriveFiles(folderId?: string, sourceType?: 'google_drive' | 'google_meet'): Promise<ApiResponse<{
    data: {
      folders: Array<{ id: string; name: string; mimeType: string; modifiedTime?: string }>;
      files: Array<{ id: string; name: string; mimeType: string; modifiedTime?: string; size?: string }>;
      nextPageToken?: string;
    };
  }>> {
    const params = new URLSearchParams();
    if (folderId) params.set('folderId', folderId);
    if (sourceType) params.set('sourceType', sourceType);
    const paramStr = params.toString();
    return this.request(`/api/google/files${paramStr ? `?${paramStr}` : ''}`);
  }

  async getGoogleDriveSelected(sourceType?: 'google_drive' | 'google_meet'): Promise<ApiResponse<{
    data: {
      selectedFiles: Array<{ id: string; name: string; mimeType: string }>;
    };
  }>> {
    const params = sourceType ? `?sourceType=${sourceType}` : '';
    return this.request(`/api/google/selected${params}`);
  }

  async saveGoogleDriveSelected(
    selectedFiles: Array<{ id: string; name: string; mimeType: string }>,
    sourceType?: 'google_drive' | 'google_meet'
  ): Promise<ApiResponse<unknown>> {
    return this.request('/api/google/selected', {
      method: 'POST',
      body: JSON.stringify({ selectedFiles, sourceType }),
    });
  }

  async findMeetRecordingsFolder(): Promise<ApiResponse<{ data: { folderId: string | null; folderName: string | null } }>> {
    return this.request<{ data: { folderId: string | null; folderName: string | null } }>('/api/google/meet-recordings-folder');
  }

  // ============================================
  // tldv API
  // ============================================

  async connectTldv(apiKey: string): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>('/api/tldv/connect', {
      method: 'POST',
      body: JSON.stringify({ apiKey }),
    });
  }

  async listTldvMeetings(params?: { page?: number; limit?: number; query?: string }): Promise<ApiResponse<{
    data: { results: TldvMeeting[]; total: number; pages: number };
  }>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.query) query.set('query', params.query);
    const queryStr = query.toString();
    return this.request(`/api/tldv/meetings${queryStr ? `?${queryStr}` : ''}`);
  }

  async saveTldvSelected(meetings: TldvMeeting[]): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>('/api/tldv/selected', {
      method: 'POST',
      body: JSON.stringify({ selectedMeetings: meetings }),
    });
  }

  async getTldvSelected(): Promise<ApiResponse<{ data: { selectedMeetings: TldvMeeting[] } }>> {
    return this.request('/api/tldv/selected');
  }

  async disconnectTldv(): Promise<ApiResponse<unknown>> {
    return this.request('/api/tldv/disconnect', {
      method: 'POST',
    });
  }

  // ============================================
  // Granola OAuth API
  // ============================================

  async getGranolaAuthUrl(): Promise<ApiResponse<{ data: { authUrl: string } }>> {
    return this.request<{ data: { authUrl: string } }>('/api/granola/auth');
  }

  async disconnectGranola(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/granola/disconnect', {
      method: 'POST',
    });
  }

  // ============================================
  // Otter.ai OAuth API
  // ============================================

  async getOtterAuthUrl(): Promise<ApiResponse<{ data: { authUrl: string } }>> {
    return this.request<{ data: { authUrl: string } }>('/api/otter/auth');
  }

  async disconnectOtter(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>('/api/otter/disconnect', {
      method: 'POST',
    });
  }

  // ============================================
  // LinkedIn API
  // ============================================

  async getLinkedInAuthUrl(): Promise<ApiResponse<{ data: { authUrl: string } }>> {
    return this.request('/api/linkedin/auth');
  }

  async getLinkedInStatus(): Promise<ApiResponse<{ data: LinkedInConnection }>> {
    return this.request('/api/linkedin/status');
  }

  async disconnectLinkedIn(): Promise<ApiResponse<unknown>> {
    return this.request('/api/linkedin/disconnect', {
      method: 'POST',
    });
  }

  async publishToLinkedIn(postId: string): Promise<ApiResponse<{ data: PostDraft }>> {
    return this.request(`/api/linkedin/publish/${postId}`, {
      method: 'POST',
    });
  }

  // ============================================
  // Integrations API (Notion, Fathom, Gong, Fireflies, Otter, Avoma, Zoom)
  // ============================================

  async connectIntegration(type: string, credentials: Record<string, string>): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>(`/api/integrations/${type}/connect`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async listIntegrationItems(type: string, params?: { page?: number; limit?: number; query?: string }): Promise<ApiResponse<{
    data: { items: PickerItem[]; total: number; pages: number };
  }>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.query) query.set('query', params.query);
    const queryStr = query.toString();
    return this.request(`/api/integrations/${type}/items${queryStr ? `?${queryStr}` : ''}`);
  }

  async saveIntegrationSelected(type: string, items: PickerItem[]): Promise<ApiResponse<SourceResponse>> {
    return this.request<SourceResponse>(`/api/integrations/${type}/selected`, {
      method: 'POST',
      body: JSON.stringify({ selectedItems: items }),
    });
  }

  async disconnectIntegration(type: string): Promise<ApiResponse<unknown>> {
    return this.request(`/api/integrations/${type}/disconnect`, {
      method: 'POST',
    });
  }

  // ============================================
  // Pipeline API (async post generation)
  // ============================================

  async startPipeline(count: number, sourceIds?: string[], context?: GenerationContext, sourceItemName?: string): Promise<ApiResponse<{ pipelineId: string }>> {
    const payload: { count: number; source_ids?: string[]; source_item_name?: string; context?: GenerationContext } = { count };
    if (sourceIds && sourceIds.length > 0) {
      payload.source_ids = sourceIds;
    }
    if (sourceItemName) {
      payload.source_item_name = sourceItemName;
    }
    if (context && (context.perspective || context.additionalContext)) {
      payload.context = context;
    }
    return this.request<{ pipelineId: string }>('/api/pipeline/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ============================================
  // Billing
  // ============================================

  async getBillingStatus(): Promise<ApiResponse<{
    customerId: string;
    subscriptions: Array<{
      id: string;
      status: string;
      planId: string;
      currentPeriodStart?: string;
      currentPeriodEnd?: string;
    }>;
    entitlements: Array<{
      featureId: string;
      access: boolean;
      usage?: number;
      limit?: number;
    }>;
    billingDisabled: boolean;
  }>> {
    return this.request('/api/billing/status');
  }

  async createCheckoutSession(planId: string): Promise<ApiResponse<{
    checkoutUrl: string;
    sessionId: string;
  }>> {
    return this.request('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }

  async verifyCheckout(sessionId: string): Promise<ApiResponse<{
    verified: boolean;
    planId?: string;
    status?: string;
  }>> {
    return this.request('/api/billing/verify-checkout', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async getBillingPortalUrl(): Promise<ApiResponse<{
    portalUrl: string;
  }>> {
    return this.request('/api/billing/portal');
  }

  async getBillingUsage(): Promise<ApiResponse<Array<{
    feature: string;
    usage: number;
    limit?: number;
    access: boolean;
    percentUsed: number;
  }>>> {
    return this.request('/api/billing/usage');
  }

  async getTrialStatus(): Promise<ApiResponse<{
    data: {
      isTrial: boolean;
      trialEndsAt: string | null;
      trialExpired: boolean;
      daysRemaining: number | null;
    };
  }>> {
    return this.request('/api/billing/trial-status');
  }

  async getAvailablePlans(): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
    currency: string;
    billing_interval: string;
    features: Array<{
      feature_id: string;
      feature_name: string;
      limit_value: number;
    }>;
  }>>> {
    return this.request('/api/billing/plans');
  }

  // ============================================
  // Admin Pricing Management
  // ============================================

  async getAdminPlans(): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
    currency: string;
    billing_interval: string;
    stripe_price_id: string | null;
    stripe_product_id: string | null;
    is_active: boolean;
    sort_order: number;
    features: Array<{
      id: string;
      plan_id: string;
      feature_id: string;
      feature_name: string;
      limit_value: number;
    }>;
  }>>> {
    return this.request('/api/admin/pricing/plans');
  }

  async updateAdminPlan(planId: string, data: {
    name?: string;
    description?: string;
    price_cents?: number;
    currency?: string;
    billing_interval?: string;
    is_active?: boolean;
    features?: Array<{
      feature_id: string;
      feature_name: string;
      limit_value: number;
    }>;
  }): Promise<ApiResponse<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
    currency: string;
    billing_interval: string;
    stripe_price_id: string | null;
    features: Array<{
      feature_id: string;
      feature_name: string;
      limit_value: number;
    }>;
  }>> {
    return this.request(`/api/admin/pricing/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async syncPlanToStripe(planId: string): Promise<ApiResponse<{
    message: string;
    plan: {
      id: string;
      stripe_price_id: string;
      stripe_product_id: string;
    };
  }>> {
    return this.request(`/api/admin/pricing/plans/${planId}/sync-stripe`, {
      method: 'POST',
    });
  }

  async getStripePriceHistory(): Promise<ApiResponse<Array<{
    id: string;
    plan_id: string;
    stripe_price_id: string;
    price_cents: number;
    currency: string;
    billing_interval: string;
    is_active: boolean;
    created_at: string;
  }>>> {
    return this.request('/api/admin/pricing/stripe-history');
  }

  // ============================================
  // Admin - Trial Management
  // ============================================

  async extendTrial(userId: string, days: number): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/users/${userId}/extend-trial`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
  }

  // ============================================
  // Admin - Email Templates
  // ============================================

  async getEmailTemplates(): Promise<ApiResponse<unknown>> {
    return this.request('/api/admin/email-templates');
  }

  async updateEmailTemplate(slug: string, data: { subject?: string; html?: string }): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/email-templates/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async testEmailTemplate(slug: string): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/email-templates/${slug}/test`, {
      method: 'POST',
    });
  }

  // ============================================
  // Admin - Release Notes
  // ============================================

  async getAdminReleaseNotes(): Promise<ApiResponse<unknown>> {
    return this.request('/api/admin/release-notes');
  }

  async createReleaseNote(data: { version: string; title: string; content?: string; features?: Array<{ title: string; description: string }> }): Promise<ApiResponse<unknown>> {
    return this.request('/api/admin/release-notes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReleaseNote(id: string, data: { version?: string; title?: string; content?: string; features?: Array<{ title: string; description: string }> }): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/release-notes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async publishReleaseNote(id: string): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/release-notes/${id}/publish`, {
      method: 'POST',
    });
  }

  async deleteReleaseNote(id: string): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/release-notes/${id}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Release Notes (user-facing)
  // ============================================

  async getLatestReleaseNote(): Promise<ApiResponse<unknown>> {
    return this.request('/api/release-notes/latest');
  }

  // ============================================
  // Skills marketplace
  // ============================================

  async listSkillCategories(): Promise<ApiResponse<{ data: SkillCategory[] }>> {
    return this.request<{ data: SkillCategory[] }>('/api/skills/categories');
  }

  async listSkills(params?: { category?: string; tool?: string; q?: string; featured?: boolean }): Promise<ApiResponse<{ data: SkillSummary[] }>> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.tool) qs.set('tool', params.tool);
    if (params?.q) qs.set('q', params.q);
    if (params?.featured) qs.set('featured', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<{ data: SkillSummary[] }>(`/api/skills${suffix}`);
  }

  async getSkill(slug: string): Promise<ApiResponse<{ data: SkillDetail }>> {
    return this.request<{ data: SkillDetail }>(`/api/skills/${encodeURIComponent(slug)}`);
  }

  async getMySkills(): Promise<ApiResponse<{ data: SkillSummary[] }>> {
    return this.request<{ data: SkillSummary[] }>('/api/skills/mine');
  }

  async acquireSkill(slug: string, mode: 'subscription' | 'one_time'): Promise<ApiResponse<{ data: { owned: boolean; mode: string } }>> {
    return this.request<{ data: { owned: boolean; mode: string } }>(`/api/skills/${encodeURIComponent(slug)}/acquire`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  }

  async releaseSkill(slug: string): Promise<ApiResponse<{ data: { owned: boolean } }>> {
    return this.request<{ data: { owned: boolean } }>(`/api/skills/${encodeURIComponent(slug)}/release`, { method: 'POST' });
  }

  async getSkillPackage(slug: string): Promise<ApiResponse<{ data: { slug: string; files: SkillFile[] } }>> {
    return this.request<{ data: { slug: string; files: SkillFile[] } }>(`/api/skills/${encodeURIComponent(slug)}/package`);
  }

  async runSkill(slug: string, input?: string): Promise<ApiResponse<{ data: { id: string; output: string; created_at: string } }>> {
    return this.request<{ data: { id: string; output: string; created_at: string } }>(`/api/skills/${encodeURIComponent(slug)}/run`, { method: 'POST', body: JSON.stringify({ input }) });
  }
  async listSkillOutputs(slug: string): Promise<ApiResponse<{ data: SkillOutput[] }>> {
    return this.request<{ data: SkillOutput[] }>(`/api/skills/${encodeURIComponent(slug)}/outputs`);
  }

  // ---- Company profile ----
  async getProfile(): Promise<ApiResponse<{ data: CompanyProfile }>> {
    return this.request<{ data: CompanyProfile }>('/api/profile');
  }
  async updateProfile(patch: Partial<CompanyProfile>): Promise<ApiResponse<{ data: CompanyProfile }>> {
    return this.request<{ data: CompanyProfile }>('/api/profile', { method: 'PATCH', body: JSON.stringify(patch) });
  }
  async enrichProfile(url: string): Promise<ApiResponse<{ data: { profile: CompanyProfile; draft: Partial<CompanyProfile> } }>> {
    return this.request<{ data: { profile: CompanyProfile; draft: Partial<CompanyProfile> } }>('/api/profile/enrich', { method: 'POST', body: JSON.stringify({ url }) });
  }
  async setProfileStage(stage: string, primary_goal?: string): Promise<ApiResponse<{ data: CompanyProfile }>> {
    return this.request<{ data: CompanyProfile }>('/api/profile/stage', { method: 'POST', body: JSON.stringify({ stage, primary_goal }) });
  }

  // ---- Admin curation ----
  async adminListSkills(): Promise<ApiResponse<{ data: { skills: any[]; categories: SkillCategory[] } }>> {
    return this.request<{ data: { skills: any[]; categories: SkillCategory[] } }>('/api/admin/skills');
  }
  async adminGetSkill(id: string): Promise<ApiResponse<{ data: any }>> {
    return this.request<{ data: any }>(`/api/admin/skills/${id}`);
  }
  async adminCreateSkill(fields: Record<string, unknown>): Promise<ApiResponse<{ data: any }>> {
    return this.request<{ data: any }>('/api/admin/skills', { method: 'POST', body: JSON.stringify(fields) });
  }
  async adminUpdateSkill(id: string, fields: Record<string, unknown>): Promise<ApiResponse<{ data: any }>> {
    return this.request<{ data: any }>(`/api/admin/skills/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
  }
  async adminSetSkillFiles(id: string, files: SkillFile[]): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/skills/${id}/files`, { method: 'PUT', body: JSON.stringify({ files }) });
  }
  async adminSetSkillGuides(id: string, guides: SkillGuide[]): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/skills/${id}/guides`, { method: 'PUT', body: JSON.stringify({ guides }) });
  }
  async adminPublishSkill(id: string, unpublish?: boolean): Promise<ApiResponse<{ data: any }>> {
    return this.request<{ data: any }>(`/api/admin/skills/${id}/publish`, { method: 'POST', body: JSON.stringify({ unpublish: !!unpublish }) });
  }
  async adminDeleteSkill(id: string): Promise<ApiResponse<unknown>> {
    return this.request(`/api/admin/skills/${id}`, { method: 'DELETE' });
  }

  /** Fetch the skill .zip with auth and trigger a browser download. */
  async downloadSkill(slug: string): Promise<void> {
    const token = await getAccessToken();
    const res = await fetch(`${this.baseUrl}/api/skills/${encodeURIComponent(slug)}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export const api = new ApiClient(API_BASE);

// ============================================
// Pipeline SSE streaming (standalone function — uses fetch + ReadableStream)
// ============================================

export interface PipelineProgress {
  phase: 'syncing' | 'generating' | 'complete' | 'failed';
  syncTotal: number;
  syncCompleted: number;
  generateTotal: number;
  generateCompleted: number;
  error?: string;
}

export async function streamPipelineEvents(
  pipelineId: string,
  onProgress: (progress: PipelineProgress) => void,
  onError: (error: string) => void,
): Promise<() => void> {
  let aborted = false;
  const controller = new AbortController();

  const cancel = () => {
    aborted = true;
    controller.abort();
  };

  (async () => {
    try {
      const token = await getAccessToken();
      const streamHeaders: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      };
      const viewAsUserId = getViewAsUserId();
      if (viewAsUserId) {
        streamHeaders['X-View-As-User'] = viewAsUserId;
      }
      const response = await fetch(`${API_BASE}/api/pipeline/${pipelineId}/events`, {
        headers: streamHeaders,
        signal: controller.signal,
      });

      if (!response.ok) {
        onError(`Pipeline stream failed: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as PipelineProgress;
              onProgress(data);

              if (data.phase === 'complete' || data.phase === 'failed') {
                cancel();
                return;
              }
            } catch {
              // Ignore malformed JSON lines
            }
          }
        }
      }
    } catch (err) {
      if (!aborted) {
        onError(err instanceof Error ? err.message : 'Stream error');
      }
    }
  })();

  return cancel;
}
